import { useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { useVerification } from "../../context/VerificationContext";
import { isUrl } from "../../lib/url";
import { useRateLimitState } from "../../hooks/useRateLimitState";
import type { StoredRateLimitState } from "../../lib/rateLimitStorage";
import { logMetricEvent } from "../../lib/delivery";
import { MetricEvents } from "@shared/integrations/delivery_keys";
import { usePageView } from "../../hooks/usePageView";
import { ROUTES } from "../../route_paths";

/** Mirrors the backend's max claim length (see claims/routes/extract.ts). */
export const MAX_CLAIM_LENGTH = 1000;

interface UseLandingResult {
  input: string;
  setInput: (value: string) => void;
  handleSubmit: (e: React.FormEvent) => void;
  rateLimitState: StoredRateLimitState | null;
  minutesUntilReset: number | null;
}

export function useLanding(): UseLandingResult {
  usePageView("landing");
  const [input, setInput] = useState("");
  const { setRawInput, setSourceUrl, setExtractedText } = useVerification();
  const navigate = useNavigate();
  const { rateLimitState, minutesUntilReset } = useRateLimitState();

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = input.trim();
    if (!trimmed || trimmed.length > MAX_CLAIM_LENGTH) return;

    setExtractedText(null);
    setRawInput(trimmed);

    const inputIsUrl = isUrl(trimmed);

    Sentry.addBreadcrumb({
      category: "navigation",
      message: "Input submitted on landing",
      level: "info",
      data: { isUrl: inputIsUrl },
    });

    logMetricEvent(MetricEvents.platformClaimSubmitted, {
      metadata: { type: inputIsUrl ? "url" : "text" },
    });

    setSourceUrl(inputIsUrl ? trimmed : null);
    // Extraction + verification run in one call from the /fetching loading screen.
    navigate(ROUTES.fetching.path());
  }

  return { input, setInput, handleSubmit, rateLimitState, minutesUntilReset };
}
